"use client";
import React from "react";
import { motion } from "motion/react";
import {
  ArrowLeft,
  Shield,
  Eye,
  Lock,
  Database,
  UserCheck,
  Globe,
  Bell,
  FileCheck,
} from "lucide-react";
import Link from "next/link";
import { GlassCard } from "@/src/components/GlassCard";
import { useTranslation } from "react-i18next";

export const PrivacyPolicyPage: React.FC = () => {
  const { t } = useTranslation("common");
  const sections = [
    {
      title: "1. Information We Collect",
      icon: Database,
      content: `
        <h4>Personal Information</h4>
        <p>We collect information that you provide directly to us when you:</p>
        <ul>
          <li><strong>Create an account:</strong> Name, email address, company name, phone number, billing address</li>
          <li><strong>Request a quote:</strong> Product specifications, quantity requirements, delivery preferences</li>
          <li><strong>Use our design studio:</strong> Uploaded designs, custom artwork, template selections</li>
          <li><strong>Place an order:</strong> Shipping address, payment information, purchase history</li>
          <li><strong>Contact us:</strong> Name, email, message content, support tickets</li>
        </ul>
        <h4>Automatically Collected Information</h4>
        <p>When you access our Service, we automatically collect:</p>
        <ul>
          <li>Log data (IP address, browser type, pages visited, time spent)</li>
          <li>Device information (device type, operating system, unique device identifiers)</li>
          <li>Cookies and similar tracking technologies</li>
          <li>Usage patterns and preferences</li>
        </ul>
      `,
    },
    {
      title: "2. How We Use Your Information",
      icon: Eye,
      content: `
        <p>We use the information we collect to:</p>
        <ul>
          <li><strong>Provide our Service:</strong> Process orders, generate quotes, deliver products</li>
          <li><strong>Improve our Service:</strong> Analyze usage patterns, develop new features, enhance user experience</li>
          <li><strong>Communicate with you:</strong> Send order updates, respond to inquiries, provide customer support</li>
          <li><strong>Marketing:</strong> Send promotional emails (with your consent), personalize marketing messages</li>
          <li><strong>Security:</strong> Detect fraud, prevent abuse, protect our users and Service</li>
          <li><strong>Legal compliance:</strong> Comply with laws, regulations, and legal processes</li>
        </ul>
        <p>We will never sell your personal information to third parties.</p>
      `,
    },
    {
      title: "3. Information Sharing and Disclosure",
      icon: Globe,
      content: `
        <p>We may share your information with:</p>
        <h4>Service Providers</h4>
        <ul>
          <li>Payment processors (to process transactions)</li>
          <li>Shipping carriers (to deliver your orders)</li>
          <li>Cloud hosting services (to store data securely)</li>
          <li>Email service providers (to send communications)</li>
          <li>Analytics providers (to understand Service usage)</li>
        </ul>
        <h4>Business Transfers</h4>
        <p>If we are involved in a merger, acquisition, or sale of assets, your information may be transferred as part of that transaction.</p>
        <h4>Legal Requirements</h4>
        <p>We may disclose your information if required by law or in response to valid legal requests, such as:</p>
        <ul>
          <li>Subpoenas or court orders</li>
          <li>Law enforcement requests</li>
          <li>Protection of our rights and safety</li>
          <li>Enforcement of our Terms of Service</li>
        </ul>
      `,
    },
    {
      title: "4. Data Security",
      icon: Lock,
      content: `
        <p>We take the security of your information seriously and implement appropriate technical and organizational measures:</p>
        <ul>
          <li><strong>Encryption:</strong> Data transmitted to and from our Service is encrypted using SSL/TLS</li>
          <li><strong>Access controls:</strong> Restricted access to personal information on a need-to-know basis</li>
          <li><strong>Secure storage:</strong> Data stored on secure servers with regular backups</li>
          <li><strong>Regular audits:</strong> Security assessments and vulnerability testing</li>
          <li><strong>Employee training:</strong> Staff trained on data protection and privacy practices</li>
        </ul>
        <p>However, no method of transmission over the Internet is 100% secure. While we strive to protect your personal information, we cannot guarantee its absolute security.</p>
      `,
    },
    {
      title: "5. Cookies and Tracking Technologies",
      icon: Eye,
      content: `
        <h4>What Are Cookies?</h4>
        <p>Cookies are small text files stored on your device that help us improve your experience on our Service.</p>
        <h4>Types of Cookies We Use</h4>
        <ul>
          <li><strong>Essential cookies:</strong> Required for the Service to function properly</li>
          <li><strong>Preference cookies:</strong> Remember your settings and preferences</li>
          <li><strong>Analytics cookies:</strong> Help us understand how you use our Service</li>
          <li><strong>Marketing cookies:</strong> Deliver relevant advertisements and track campaign effectiveness</li>
        </ul>
        <h4>Managing Cookies</h4>
        <p>You can control cookies through your browser settings. Note that disabling certain cookies may limit your ability to use some features of our Service.</p>
      `,
    },
    {
      title: "6. Your Privacy Rights",
      icon: UserCheck,
      content: `
        <p>Depending on your location, you may have the following rights:</p>
        <h4>Access and Portability</h4>
        <ul>
          <li>Request a copy of the personal information we hold about you</li>
          <li>Receive your data in a portable format</li>
        </ul>
        <h4>Correction and Update</h4>
        <ul>
          <li>Correct inaccurate or incomplete information</li>
          <li>Update your account information at any time</li>
        </ul>
        <h4>Deletion</h4>
        <ul>
          <li>Request deletion of your personal information</li>
          <li>Note: We may retain certain information for legal or business purposes</li>
        </ul>
        <h4>Objection and Restriction</h4>
        <ul>
          <li>Object to processing of your personal information</li>
          <li>Restrict how we use your data</li>
        </ul>
        <h4>Opt-Out</h4>
        <ul>
          <li>Unsubscribe from marketing communications at any time</li>
          <li>Opt out of certain data sharing practices</li>
        </ul>
        <p>To exercise these rights, contact us at privacy@walnutpackaging.com</p>
      `,
    },
    {
      title: "7. Data Retention",
      icon: Database,
      content: `
        <p>We retain your personal information for as long as necessary to:</p>
        <ul>
          <li>Provide our Service and fulfill orders</li>
          <li>Comply with legal obligations</li>
          <li>Resolve disputes and enforce agreements</li>
          <li>Maintain business records</li>
        </ul>
        <h4>Retention Periods</h4>
        <ul>
          <li><strong>Account information:</strong> Retained while your account is active, plus 7 years after closure</li>
          <li><strong>Order history:</strong> Retained for 7 years for tax and accounting purposes</li>
          <li><strong>Design files:</strong> Retained while your account is active, unless you request deletion</li>
          <li><strong>Marketing data:</strong> Retained until you opt out or request deletion</li>
        </ul>
      `,
    },
    {
      title: "8. Children's Privacy",
      icon: Shield,
      content: `
        <p>Our Service is not intended for individuals under the age of 18. We do not knowingly collect personal information from children under 18.</p>
        <p>If you are a parent or guardian and believe your child has provided us with personal information, please contact us immediately. We will take steps to delete such information from our systems.</p>
      `,
    },
    {
      title: "9. International Data Transfers",
      icon: Globe,
      content: `
        <p>Your information may be transferred to and processed in countries other than your country of residence. These countries may have data protection laws different from those in your country.</p>
        <p>We ensure that such transfers comply with applicable data protection laws and implement appropriate safeguards, such as:</p>
        <ul>
          <li>Standard contractual clauses approved by regulatory authorities</li>
          <li>Adequacy decisions by relevant regulatory bodies</li>
          <li>Privacy Shield frameworks (where applicable)</li>
        </ul>
      `,
    },
    {
      title: "10. Third-Party Links",
      icon: Globe,
      content: `
        <p>Our Service may contain links to third-party websites, applications, or services. We are not responsible for the privacy practices of these third parties.</p>
        <p>We encourage you to read the privacy policies of any third-party sites you visit. This Privacy Policy applies only to information collected by our Service.</p>
      `,
    },
    {
      title: "11. California Privacy Rights",
      icon: FileCheck,
      content: `
        <p>If you are a California resident, you have additional rights under the California Consumer Privacy Act (CCPA):</p>
        <ul>
          <li>Right to know what personal information we collect, use, and share</li>
          <li>Right to delete personal information (subject to certain exceptions)</li>
          <li>Right to opt-out of the sale of personal information (we do not sell your information)</li>
          <li>Right to non-discrimination for exercising your privacy rights</li>
        </ul>
        <p>To exercise your California privacy rights, contact us at privacy@walnutpackaging.com or call +1 (555) 123-4567.</p>
      `,
    },
    {
      title: "12. Changes to This Privacy Policy",
      icon: Bell,
      content: `
        <p>We may update this Privacy Policy from time to time to reflect changes in our practices or for legal, operational, or regulatory reasons.</p>
        <p>We will notify you of material changes by:</p>
        <ul>
          <li>Posting the updated Privacy Policy on this page</li>
          <li>Updating the "Last Updated" date at the top</li>
          <li>Sending an email notification to registered users</li>
          <li>Displaying a prominent notice on our Service</li>
        </ul>
        <p>Your continued use of our Service after any changes constitutes your acceptance of the updated Privacy Policy.</p>
      `,
    },
    {
      title: "13. Contact Us",
      icon: Shield,
      content: `
        <p>If you have any questions, concerns, or requests regarding this Privacy Policy or our data practices, please contact us:</p>
        <ul>
          <li><strong>Email:</strong> privacy@walnutpackaging.com</li>
          <li><strong>Phone:</strong> +1 (555) 123-4567</li>
          <li><strong>Mail:</strong> Walnut Packaging Factory<br/>Attn: Privacy Officer<br/>123 Packaging Boulevard, Suite 100<br/>Manufacturing City, ST 12345</li>
        </ul>
        <p>We will respond to your inquiry within 30 days.</p>
      `,
    },
  ];

  return (
    <div className="min-h-screen pt-32 pb-16 px-4 sm:px-6 lg:px-8">
      <div className="max-w-4xl mx-auto">
        {/* Back Button */}
        <motion.div
          initial={{ opacity: 0, x: -20 }}
          animate={{ opacity: 1, x: 0 }}
          className="mb-8"
        >
          <Link
            href="/"
            className="inline-flex items-center gap-2 text-walnut-700 dark:text-walnut-300 hover:text-walnut-900 dark:hover:text-white transition-colors"
          >
            <ArrowLeft className="w-5 h-5" />
            <span className="font-medium">{t("marketingPages.privacy.back")}</span>
          </Link>
        </motion.div>

        {/* Header */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.1 }}
          className="text-center mb-12"
        >
          <div className="inline-flex items-center justify-center w-16 h-16 rounded-2xl bg-gradient-to-br from-walnut-500 to-walnut-700 mb-6">
            <Shield className="w-8 h-8 text-white" />
          </div>
          <h1 className="text-4xl md:text-5xl font-bold text-walnut-900 dark:text-white mb-4">
            {t("marketingPages.privacy.title")}
          </h1>
          <p className="text-lg text-walnut-700 dark:text-walnut-300">
            {t("marketingPages.privacy.lastUpdated")}
          </p>
        </motion.div>

        {/* Introduction */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.2 }}
        >
          <GlassCard className="p-6 mb-8">
            <p className="text-walnut-700 dark:text-walnut-300 leading-relaxed mb-4">
              {t("marketingPages.privacy.intro1")}
            </p>
            <p className="text-walnut-700 dark:text-walnut-300 leading-relaxed">
              {t("marketingPages.privacy.intro2")}
            </p>
          </GlassCard>
        </motion.div>

        {/* Privacy Sections */}
        <div className="space-y-6">
          {sections.map((section, index) => (
            <motion.div
              key={section.title}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.1 * (index + 3) }}
            >
              <GlassCard className="p-8">
                <div className="flex items-start gap-4 mb-4">
                  <div className="w-10 h-10 rounded-xl bg-walnut-100 dark:bg-walnut-800 flex items-center justify-center flex-shrink-0">
                    <section.icon className="w-5 h-5 text-walnut-700 dark:text-walnut-300" />
                  </div>
                  <h2 className="text-2xl font-bold text-walnut-900 dark:text-white">
                    {section.title}
                  </h2>
                </div>
                <div
                  className="prose prose-sm dark:prose-invert max-w-none
                    prose-headings:text-walnut-900 dark:prose-headings:text-white
                    prose-h4:text-lg prose-h4:font-semibold prose-h4:mb-2 prose-h4:mt-4
                    prose-p:text-walnut-700 dark:prose-p:text-walnut-300 prose-p:mb-3 prose-p:leading-relaxed
                    prose-li:text-walnut-700 dark:prose-li:text-walnut-300
                    prose-strong:text-walnut-900 dark:prose-strong:text-white
                    prose-ul:my-3 prose-ul:ml-6"
                  dangerouslySetInnerHTML={{ __html: section.content }}
                />
              </GlassCard>
            </motion.div>
          ))}
        </div>

        {/* Footer CTA */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.3 }}
          className="mt-12"
        >
          <GlassCard className="p-8 text-center">
            <h3 className="text-2xl font-bold text-walnut-900 dark:text-white mb-3">
              {t("marketingPages.privacy.ctaTitle")}
            </h3>
            <p className="text-walnut-700 dark:text-walnut-300 mb-6">
              {t("marketingPages.privacy.ctaSubtitle")}
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Link
                href="/contact"
                className="inline-flex items-center justify-center gap-2 px-8 py-4 rounded-[33px] bg-gradient-to-r from-walnut-600 to-walnut-700 dark:from-walnut-500 dark:to-walnut-600 text-white font-semibold hover:shadow-lg hover:shadow-walnut-500/30 transition-all duration-300 hover:scale-105"
              >
                {t("marketingPages.privacy.ctaContact")}
              </Link>
              <Link
                href="/terms"
                className="inline-flex items-center justify-center gap-2 px-8 py-4 rounded-[33px] backdrop-blur-xl bg-white/20 dark:bg-black/20 border border-white/30 dark:border-white/10 text-walnut-900 dark:text-white font-semibold hover:bg-white/30 dark:hover:bg-black/30 transition-all duration-300 hover:scale-105"
              >
                {t("marketingPages.privacy.ctaTerms")}
              </Link>
            </div>
          </GlassCard>
        </motion.div>
      </div>
    </div>
  );
};
